<template>
  <div class="cau-card">
    <!-- HEADER -->
    <div class="cau-header">
      <div class="left">
        <div class="title">
          <i class="fa fa-industry mr-1"></i> Cẩu hàng
        </div>
        <div class="sub">
          Tài xế:
          <strong>{{ taiXe?.hoTen || ("ID: " + (taiXeId || "—")) }}</strong>
        </div>
        <div class="sub">
          Xe: <strong>{{ xeBienSo || "—" }}</strong>
        </div>
      </div>

      <div class="right">
        <span v-if="hasActive" class="badge badge-active">Đang cẩu</span>
        <span v-else class="badge badge-idle">Rảnh</span>
      </div>
    </div>

    <!-- BODY: LOADING / ERROR -->
    <div v-if="isLoadingActive" class="state-row">
      <span class="spinner-border spinner-border-sm mr-2"></span>
      Đang kiểm tra phiên cẩu hiện tại...
    </div>

    <div v-else-if="activeError" class="state-row text-danger">
      <i class="fa fa-exclamation-circle mr-1"></i>{{ activeError }}
    </div>

    <!-- BODY: ĐANG CÓ PHIÊN CẨU -->
    <div v-else-if="hasActive && activeSession" class="session-box">
      <div class="row-line">
        <span class="label">Phiên #</span>
        <span class="value">#{{ activeSession.id }}</span>
      </div>

      <div class="row-line">
        <span class="label">Bắt đầu</span>
        <span class="value">
          {{ fmtDateTime(activeSession.startedAt || activeSession.createdAt) }}
        </span>
      </div>

      <div class="row-line">
        <span class="label">Ghi chú mở</span>
        <span class="value">{{ activeSession.ghiChu || "—" }}</span>
      </div>

      <!-- NÚT ĐÓNG CẨU -> MỞ MODAL -->
      <button
        class="btn-main btn-danger"
        type="button"
        :disabled="isSubmittingClose"
        @click="openCloseModal"
      >
        <span
          v-if="isSubmittingClose"
          class="spinner-border spinner-border-sm mr-2"
        ></span>
        <i v-else class="fa fa-stop mr-2"></i>
        Kết thúc phiên cẩu
      </button>
    </div>

    <!-- BODY: CHƯA CÓ PHIÊN CẨU -->
    <div v-else class="session-box empty">
      <div class="row-line">
        <span class="label">Trạng thái</span>
        <span class="value">Chưa có phiên cẩu</span>
      </div>
      <div class="hint">
        Nhấn nút bên dưới để bắt đầu một phiên cẩu mới cho xe
        <strong>{{ xeBienSo || "—" }}</strong>.
      </div>

      <button
        class="btn-main btn-primary"
        type="button"
        @click="showOpenModal = true"
      >
        <i class="fa fa-play mr-2"></i>
        Bắt đầu phiên cẩu
      </button>
    </div>

    <!-- LỊCH SỬ CẨU GẦN ĐÂY -->
    <div class="history-box">
      <div class="history-header">
        <div class="left">
          <i class="fa fa-history mr-1"></i>
          Lịch sử cẩu gần đây
        </div>
        <button
          type="button"
          class="history-reload"
          @click="loadHistory"
          :disabled="isLoadingHistory"
        >
          <i
            class="fa fa-refresh"
            :class="{ 'fa-spin': isLoadingHistory }"
          ></i>
        </button>
      </div>

      <div v-if="isLoadingHistory" class="history-state small text-muted">
        <span class="spinner-border spinner-border-sm mr-1"></span>
        Đang tải lịch sử...
      </div>

      <div v-else-if="historyError" class="history-state text-danger small">
        <i class="fa fa-exclamation-circle mr-1"></i>{{ historyError }}
      </div>

      <div
        v-else-if="!recentHistory.length"
        class="history-state small text-muted"
      >
        Chưa có dữ liệu lịch sử.
      </div>

      <ul v-else class="history-list">
        <li v-for="h in recentHistory" :key="h.id" class="history-item">
          <div class="h-row-top">
            <div class="h-title">
              Phiên #{{ h.id }} • {{ h.xeBienSo || ("Xe #" + h.xeId) }}
            </div>
            <span
              class="h-status"
              :class="{
                'h-open': h.status === 'OPEN',
                'h-closed': h.status === 'CLOSED'
              }"
            >
              {{ h.status === "OPEN" ? "Đang mở" : "Đã đóng" }}
            </span>
          </div>

          <div class="h-row-middle small text-muted">
            <div>
              Bắt đầu: {{ fmtDateTime(h.startedAt || h.createdAt) }}
            </div>
            <div v-if="h.endedAt">
              Kết thúc: {{ fmtDateTime(h.endedAt) }}
            </div>
          </div>

          <div class="h-row-bottom small">
            <span v-if="h.durationMinutes != null">
              ⏱ {{ h.durationMinutes }} phút
            </span>
            <span v-else class="text-muted">
              ⏱ Chưa tính được thời lượng
            </span>

            <span class="h-links">
              <button
                v-if="h.imgStartUrl"
                type="button"
                class="link-btn"
                @click="openImagePreview(h.imgStartUrl)"
              >
                Ảnh mở
              </button>
              <button
                v-if="h.imgEndUrl"
                type="button"
                class="link-btn"
                @click="openImagePreview(h.imgEndUrl)"
              >
                Ảnh đóng
              </button>
            </span>
          </div>
        </li>
      </ul>
    </div>

    <!-- LỖI SUBMIT -->
    <div v-if="submitError" class="submit-error">
      <i class="fa fa-exclamation-triangle mr-1"></i>{{ submitError }}
    </div>

    <!-- MODAL MỞ CẨU -->
    <transition name="fade-fast">
      <div
        v-if="showOpenModal"
        class="sheet-overlay"
        @click.self="closeOpenModal"
      >
        <div class="sheet-card">
          <div class="sheet-header">
            <div class="sheet-title">
              <i class="fa fa-play mr-1"></i> Bắt đầu phiên cẩu
            </div>
            <button
              class="sheet-close"
              type="button"
              @click="closeOpenModal"
              :disabled="isSubmittingOpen || uploadingOpen"
            >
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="sheet-body">
            <div class="sheet-row">
              <div class="text-small text-muted">
                Xe:
                <strong>{{ xeBienSo || ("#" + xeId) }}</strong><br />
                Tài xế:
                <strong>
                  {{ taiXe?.hoTen || ("ID: " + (taiXeId || "—")) }}
                </strong>
              </div>
            </div>

            <!-- Camera mở cẩu -->
            <div class="camera-block">
              <div class="camera-label">
                Ảnh xác minh mở cẩu <span class="text-danger">*</span>
              </div>

              <div class="camera-row">
                <button
                  type="button"
                  class="capture-btn"
                  @click="openCamera('open')"
                  :disabled="isSubmittingOpen || uploadingOpen"
                >
                  <span
                    v-if="uploadingOpen"
                    class="spinner-border spinner-border-sm mr-1"
                  ></span>
                  <i v-else class="fa fa-camera mr-1"></i>
                  <span>
                    {{ openForm.anhMoUrl ? "Chụp lại ảnh mở cẩu" : "Chụp ảnh mở cẩu" }}
                  </span>
                </button>

                <div v-if="openForm.anhMoUrl" class="preview-card">
                  <div class="preview-img-wrap" @click="openImagePreview(openForm.anhMoUrl)">
                    <img
                      :src="openForm.anhMoUrl"
                      class="preview-img"
                      alt="Ảnh mở cẩu"
                    />
                  </div>
                  <button
                    type="button"
                    class="preview-del"
                    @click="openForm.anhMoUrl = ''"
                    :disabled="isSubmittingOpen || uploadingOpen"
                  >
                    <i class="fa fa-trash mr-1"></i> Xóa ảnh
                  </button>
                </div>
              </div>
            </div>

            <!-- Ghi chú mở -->
            <div class="note-block">
              <label class="note-label">Ghi chú mở (không bắt buộc)</label>
              <textarea
                v-model.trim="openForm.ghiChu"
                rows="2"
                class="note-textarea"
                placeholder="VD: mở cẩu bốc hàng tại kho chính..."
              ></textarea>
            </div>

            <!-- Footer -->
            <div class="sheet-footer">
              <button
                type="button"
                class="btn-main btn-secondary"
                @click="closeOpenModal"
                :disabled="isSubmittingOpen || uploadingOpen"
              >
                Hủy
              </button>
              <button
                type="button"
                class="btn-main btn-primary"
                :disabled="isSubmittingOpen || uploadingOpen || !openForm.anhMoUrl"
                @click="handleOpenCau"
              >
                <span
                  v-if="isSubmittingOpen"
                  class="spinner-border spinner-border-sm mr-2"
                ></span>
                <i v-else class="fa fa-check mr-2"></i>
                Xác nhận bắt đầu
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- MODAL ĐÓNG CẨU -->
    <transition name="fade-fast">
      <div
        v-if="showCloseModal"
        class="sheet-overlay"
        @click.self="closeCloseModal"
      >
        <div class="sheet-card">
          <div class="sheet-header">
            <div class="sheet-title">
              <i class="fa fa-stop mr-1"></i> Kết thúc phiên cẩu
            </div>
            <button
              class="sheet-close"
              type="button"
              @click="closeCloseModal"
              :disabled="isSubmittingClose || uploadingClose"
            >
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="sheet-body">
            <div class="sheet-row">
              <div class="text-small text-muted">
                Xe:
                <strong>{{ xeBienSo || ("#" + xeId) }}</strong><br />
                Tài xế:
                <strong>
                  {{ taiXe?.hoTen || ("ID: " + (taiXeId || "—")) }}
                </strong><br />
                Phiên:
                <strong>#{{ activeSession?.id || "—" }}</strong>
              </div>
            </div>

            <!-- Camera đóng cẩu -->
            <div class="camera-block">
              <div class="camera-label">
                Ảnh xác minh khi đóng cẩu <span class="text-danger">*</span>
              </div>

              <div class="camera-row">
                <button
                  type="button"
                  class="capture-btn"
                  @click="openCamera('close')"
                  :disabled="isSubmittingClose || uploadingClose"
                >
                  <span
                    v-if="uploadingClose"
                    class="spinner-border spinner-border-sm mr-1"
                  ></span>
                  <i v-else class="fa fa-camera mr-1"></i>
                  <span>
                    {{
                      closeForm.anhDongUrl
                        ? "Chụp lại ảnh đóng cẩu"
                        : "Chụp ảnh đóng cẩu"
                    }}
                  </span>
                </button>

                <div v-if="closeForm.anhDongUrl" class="preview-card">
                  <div
                    class="preview-img-wrap"
                    @click="openImagePreview(closeForm.anhDongUrl)"
                  >
                    <img
                      :src="closeForm.anhDongUrl"
                      class="preview-img"
                      alt="Ảnh đóng cẩu"
                    />
                  </div>
                  <button
                    type="button"
                    class="preview-del"
                    @click="closeForm.anhDongUrl = ''"
                    :disabled="isSubmittingClose || uploadingClose"
                  >
                    <i class="fa fa-trash mr-1"></i> Xóa ảnh
                  </button>
                </div>
              </div>
            </div>

            <!-- Ghi chú đóng -->
            <div class="note-block">
              <label class="note-label">Ghi chú khi đóng cẩu (không bắt buộc)</label>
              <textarea
                v-model.trim="closeForm.ghiChu"
                rows="2"
                class="note-textarea"
                placeholder="VD: hoàn tất cẩu, không sự cố..."
              ></textarea>
            </div>

            <!-- Footer -->
            <div class="sheet-footer">
              <button
                type="button"
                class="btn-main btn-secondary"
                @click="closeCloseModal"
                :disabled="isSubmittingClose || uploadingClose"
              >
                Hủy
              </button>
              <button
                type="button"
                class="btn-main btn-danger"
                :disabled="isSubmittingClose || uploadingClose || !closeForm.anhDongUrl"
                @click="handleCloseCau"
              >
                <span
                  v-if="isSubmittingClose"
                  class="spinner-border spinner-border-sm mr-2"
                ></span>
                <i v-else class="fa fa-check mr-2"></i>
                Xác nhận kết thúc
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- FULLSCREEN CAMERA DÙNG CHUNG (giống TaiXeĐổXăng) -->
    <FullscreenCamera
      v-model="cameraCtx.open"
      :title="cameraTitle"
      :busy="uploadingOpen || uploadingClose"
      initial-facing="environment"
      @confirm="onCameraConfirm"
    />

    <VueEasyLightbox
      :visible="lb.visible"
      :imgs="lb.imgs"
      :index="lb.index"
      @hide="lb.visible = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useCauSessionStore } from "@/stores/cauSessionStore";
import FullscreenCamera from "@/components/FullscreenCamera.vue";
import fileService from "@/services/fileService";
import VueEasyLightbox from "vue-easy-lightbox";

// Utils datetime
function pad2(n) {
  return String(n).padStart(2, "0");
}

function parseIsoLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;

  const m = String(s).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
  );
  if (!m) return null;

  const [, y, M, d, h, mi, se] = m;
  return new Date(+y, +M - 1, +d, +h, +mi, +se);
}

function fmtDateTimeStr(s) {
  const d = parseIsoLocal(s);
  if (!d) return "";

  const datePart = `${pad2(d.getDate())}/${pad2(
    d.getMonth() + 1
  )}/${d.getFullYear()}`;
  const timePart = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${datePart} ${timePart}`;
}

export default {
  name: "TaiXeCauHangCard",
  components: { FullscreenCamera, VueEasyLightbox },

  props: {
    xeId: { type: Number, required: true },
    xeBienSo: { type: String, default: "" },
    taiXe: { type: Object, required: true },
  },

  data() {
    return {
      showOpenModal: false,
      showCloseModal: false,
      openForm: {
        anhMoUrl: "",
        ghiChu: "",
      },
      closeForm: {
        anhDongUrl: "",
        ghiChu: "",
      },
      // camera context giống TaiXeĐổXăng
      cameraCtx: {
        open: false,
        target: null, // 'open' | 'close'
      },
      uploadingOpen: false,
      uploadingClose: false,
      lb: {
        visible: false,
        imgs: [],   // danh sách url
        index: 0,   // vị trí đang xem
      },
    };
  },

  computed: {
    ...mapState(useCauSessionStore, [
      "activeSession",
      "hasActive",
      "isLoadingActive",
      "activeError",
      "isSubmittingOpen",
      "isSubmittingClose",
      "submitError",
      "history",
      "isLoadingHistory",
      "historyError",
    ]),

    taiXeId() {
      return this.taiXe?.idNguoiDung || this.taiXe?.id || null;
    },

    recentHistory() {
      if (!this.history || !this.history.length) return [];
      return this.history.slice(0, 5);
    },

    cameraTitle() {
      if (this.cameraCtx.target === "open") return "Chụp ảnh xác minh mở cẩu";
      if (this.cameraCtx.target === "close") return "Chụp ảnh xác minh đóng cẩu";
      return "Chụp ảnh";
    },
  },

  methods: {
    ...mapActions(useCauSessionStore, [
      "fetchActiveForDriver",
      "openCau",
      "closeCau",
      "fetchHistoryForDriver",
    ]),

    fmtDateTime(d) {
      return fmtDateTimeStr(d);
    },

    closeOpenModal() {
      if (this.isSubmittingOpen || this.uploadingOpen) return;
      this.showOpenModal = false;
      // không xóa ảnh, để tài xế mở lại vẫn xem được
    },

    openCloseModal() {
      if (!this.activeSession || !this.activeSession.id) {
        alert("Không có phiên cẩu nào đang mở.");
        return;
      }
      if (this.isSubmittingClose || this.uploadingClose) return;
      this.showCloseModal = true;
    },

    closeCloseModal() {
      if (this.isSubmittingClose || this.uploadingClose) return;
      this.showCloseModal = false;
    },

    async loadActive() {
      if (!this.taiXeId) {
        console.warn("[TaiXeCauHangCard] Không có taiXeId");
        return;
      }
      await this.fetchActiveForDriver(this.taiXeId);
    },

    async loadHistory() {
      if (!this.taiXeId) return;
      await this.fetchHistoryForDriver(this.taiXeId);
    },

    openImage(url) {
      if (!url) return;
      this.openImagePreview(url);
    },

    openCamera(target) {
      // target: 'open' | 'close'
      if (target === 'open' && (this.isSubmittingOpen || this.uploadingOpen)) return;
      if (target === 'close' && (this.isSubmittingClose || this.uploadingClose)) return;

      this.cameraCtx.target = target;
      this.cameraCtx.open = true;
    },


    async onCameraConfirm({ blob }) {
      // snapshot target trước, rồi reset context
      const target = this.cameraCtx.target;   // 'open' | 'close' | null

      this.cameraCtx.open = false;
      this.cameraCtx.target = null;

      if (!blob || !target) return;

      const isOpen = target === 'open';

      if (isOpen) this.uploadingOpen = true;
      else this.uploadingClose = true;

      try {
        const rawFile = new File(
          [blob],
          `${isOpen ? 'mo-cau' : 'dong-cau'}_${Date.now()}.jpg`,
          { type: 'image/jpeg' }
        );

        const compressed = await this.compressImage(rawFile, {
          maxW: 1600,
          maxH: 1600,
          quality: 0.82,
        });

        const formData = new FormData();
        formData.append('files', compressed, rawFile.name);
        formData.append('folderName', isOpen ? 'cau_session_mo' : 'cau_session_dong');

        const res = await fileService.upload(formData);
        const first = Array.isArray(res.data) ? res.data[0] : null;

        if (!first?.location) {
          throw new Error('Server không trả về URL file.');
        }

        if (isOpen) {
          this.openForm.anhMoUrl = first.location;
        } else {
          this.closeForm.anhDongUrl = first.location;
        }
      } catch (err) {
        console.error('Lỗi upload ảnh cẩu:', err);
        this.$toast?.error?.(
          err?.response?.data?.error || err?.message || 'Upload ảnh thất bại.'
        );
      } finally {
        if (isOpen) this.uploadingOpen = false;
        else this.uploadingClose = false;
      }
    },


    compressImage(file, { maxW = 1600, maxH = 1600, quality = 0.82 } = {}) {
      return new Promise((resolve, reject) => {
        try {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            let { width, height } = img;
            const ratio = Math.min(maxW / width, maxH / height, 1);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(url);
                if (!blob) return reject(new Error("Không tạo được blob ảnh."));
                const compressedFile = new File(
                  [blob],
                  file.name.replace(/\.(\w+)$/, "_min.$1"),
                  {
                    type: blob.type || "image/jpeg",
                    lastModified: Date.now(),
                  }
                );
                resolve(compressedFile);
              },
              "image/jpeg",
              quality
            );
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Không đọc được ảnh."));
          };
          img.src = url;
        } catch (err) {
          reject(err);
        }
      });
    },

    async handleOpenCau() {
      if (!this.taiXeId) {
        alert("Không xác định được tài xế. Vui lòng đăng nhập lại.");
        return;
      }
      if (!this.xeId) {
        alert("Không xác định được xe hiện tại.");
        return;
      }
      if (!this.openForm.anhMoUrl) {
        alert("Vui lòng chụp ảnh xác minh mở cẩu trước khi bắt đầu.");
        return;
      }

      const payload = {
        xeId: this.xeId,
        imgStartUrl: this.openForm.anhMoUrl,
        ghiChu: this.openForm.ghiChu || null,
      };

      try {
        await this.openCau(this.taiXeId, payload);
        this.$toast?.success?.("Đã bắt đầu phiên cẩu.");

        // ✅ RESET FORM MỞ
        this.openForm = {
          anhMoUrl: "",
          ghiChu: "",
        };

        // ✅ RESET FORM ĐÓNG ĐỂ KHÔNG DÍNH ẢNH CŨ
        this.closeForm = {
          anhDongUrl: "",
          ghiChu: "",
        };

        this.showOpenModal = false;

        await this.loadActive();
        await this.loadHistory();
      } catch (e) {
        console.error("handleOpenCau error", e);
        this.$toast?.error?.("Không mở được phiên cẩu.");
      }
    },


    async handleCloseCau() {
      if (!this.taiXeId) {
        alert("Không xác định được tài xế.");
        return;
      }
      if (!this.activeSession || !this.activeSession.id) {
        alert("Không có phiên cẩu nào đang mở.");
        return;
      }
      if (!this.closeForm.anhDongUrl) {
        alert("Vui lòng chụp ảnh xác minh khi đóng cẩu.");
        return;
      }

      const payload = {
        imgEndUrl: this.closeForm.anhDongUrl,
        ghiChu: this.closeForm.ghiChu || null,
      };

      try {
        await this.closeCau(this.taiXeId, this.activeSession.id, payload);
        this.$toast?.success?.("Đã kết thúc phiên cẩu.");

        this.closeForm.ghiChu = "";
        this.showCloseModal = false;

        await this.loadActive();
        await this.loadHistory();
      } catch (e) {
        console.error("handleCloseCau error", e);
        this.$toast?.error?.("Không kết thúc được phiên cẩu.");
      }
    },
    openImagePreview(imgs, startIndex = 0) {
      // imgs có thể là string hoặc array
      const list = Array.isArray(imgs) ? imgs.slice() : [imgs];
      if (!list.length) return;

      this.lb.imgs = list;
      this.lb.index = Math.max(0, Math.min(startIndex, list.length - 1));
      this.lb.visible = true;
    },
  },

  mounted() {
    this.loadActive();
    this.loadHistory();
  },
};
</script>

<style scoped>
.cau-card {
  padding: 12px 14px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
  font-size: 14px;
}

.cau-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.title {
  font-weight: 600;
  color: #2d3748;
  font-size: 15px;
}

.sub {
  font-size: 12px;
  color: #718096;
  margin-top: 2px;
}

.badge {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.badge-active {
  background: #c6f6d5;
  color: #22543d;
}

.badge-idle {
  background: #edf2f7;
  color: #4a5568;
}

.state-row {
  padding: 8px 0;
  font-size: 13px;
}

.session-box {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 10px 12px;
  background: #f7fafc;
}

.session-box.empty {
  background: #fefcbf;
  border-color: #faf089;
}

.row-line {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 4px;
}

.label {
  color: #718096;
}

.value {
  font-weight: 600;
  color: #2d3748;
  text-align: right;
  max-width: 60%;
}

.hint {
  font-size: 12px;
  color: #744210;
  margin-top: 6px;
  margin-bottom: 8px;
}

/* camera */
.camera-block {
  margin-top: 8px;
  margin-bottom: 8px;
}

.camera-label {
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
}

.camera-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

.capture-btn {
  background: #fff;
  border: 2px dashed #3182ce !important;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #2b6cb0 !important;
  text-align: center;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.preview-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 6px;
  font-size: 12px;
  max-width: 96px;
  margin-left: 4px;
}

.preview-img-wrap {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  overflow: hidden;
  margin-bottom: 6px;
  cursor: zoom-in;
}

.preview-img {
  width: 100%;
  height: auto;
  display: block;
}

.preview-del {
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: #e53e3e;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px;
}

/* note */
.note-block {
  margin-top: 4px;
  margin-bottom: 6px;
}

.note-label {
  font-size: 12px;
  color: #4a5568;
  font-weight: 600;
  display: block;
  margin-bottom: 3px;
}

.note-textarea {
  width: 100%;
  resize: none;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 6px 8px;
  font-size: 13px;
}

/* buttons */
.btn-main {
  width: 100%;
  margin-top: 8px;
  border-radius: 999px;
  border: none;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: #3182ce;
  color: #fff;
}

.btn-danger {
  background: #e53e3e;
  color: #fff;
}

.btn-secondary {
  background: #e2e8f0;
  color: #2d3748;
}

.btn-main:disabled {
  opacity: 0.6;
}

.submit-error {
  margin-top: 6px;
  font-size: 12px;
  color: #e53e3e;
}

/* spinner */
.spinner-border {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* history */
.history-box {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 4px;
}

.history-reload {
  border: none;
  background: #edf2f7;
  border-radius: 999px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #4a5568;
}

.history-state {
  padding: 4px 0;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.history-item {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 6px 8px;
  margin-bottom: 6px;
}

.h-row-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.h-title {
  font-weight: 600;
  color: #2d3748;
  margin-right: 6px;
}

.h-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 999px;
}

.h-open {
  background: #fefcbf;
  color: #744210;
}

.h-closed {
  background: #c6f6d5;
  color: #22543d;
}

.h-row-middle {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
}

.h-row-bottom {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.h-links {
  display: inline-flex;
  gap: 4px;
}

.link-btn {
  border: none;
  background: #edf2f7;
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #2b6cb0;
}

/* sheet / modal */
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 8px;
}

.sheet-card {
  background: #fff;
  width: 100%;
  max-width: 480px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.sheet-header {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sheet-title {
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
}

.sheet-close {
  border: none;
  background: #edf2f7;
  border-radius: 999px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #4a5568;
}

.sheet-body {
  padding: 10px 12px 12px;
}

.sheet-footer {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

/* MOBILE OPTIMIZE */
@media (max-width: 480px) {
  .camera-row {
    flex-direction: column;
    align-items: stretch;
  }

  .capture-btn {
    width: 100%;
  }

  .preview-card {
    margin-left: 0;
    margin-top: 6px;
  }
}
</style>
